/** First-time guides (docs/ui-decisions.md, "First-time guides: a card,
 *  not a screen"): a small card docked at the right, under the top bar,
 *  shown the first time something happens to a player and never again
 *  (remembered on this machine). Nothing is blocked: the map stays fully
 *  playable around it. One at a time; others wait their turn. */

import { For, Show, createSignal, type JSX } from "solid-js";

export interface Guide {
  /** What it's about, once per player ("respawn"). */
  id: string;
  title: string;
  /** Two to four short lines: what happened, then what to do. */
  lines: JSX.Element[];
  /** Where to look ("Show me" takes the camera there), if anywhere. */
  at?: [number, number];
  /** Or what "Show me" opens instead (a tab). */
  show?: () => void;
  /** Something to do about it, right on the card ("Accept"). */
  action?: { label: string; run: () => void };
}

/** The free builds, by the state's key: what they're called (plural) and
 *  what one costs once they're used up (construction.rs). */
export const FREE_KINDS: Record<string, [string, number, string]> = {
  mine: ["mines", 200, "mine"],
  station: ["stations", 150, "station"],
  barracks: ["barracks", 150, "barracks"],
  Steelworks: ["steelworks", 300, "steelworks"],
  "Munitions plant": ["munitions plants", 300, "munitions plant"],
  "Recruiting office": ["recruiting offices", 300, "recruiting office"],
};

const [guide, setGuide] = createSignal<Guide | null>(null);
export { guide };

/** Guides waiting their turn behind the one shown. */
const waiting: Guide[] = [];

/** Where "Show me" sends the camera (the game sets it). */
let look: (at: [number, number]) => void = () => {};
export function onGuideLook(f: (at: [number, number]) => void): void {
  look = f;
}

const STORE = "ferro.guides";

/** Guides seen, per player ("<player>:<id>"); empty when storage is
 *  unavailable (then a guide may show again, which is harmless). */
function seen(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(STORE) ?? "[]") as string[]);
  } catch {
    return new Set();
  }
}

/** Whether `player` has seen the guide `id` (it won't show again). */
export function guideSeen(player: string, id: string): boolean {
  return seen().has(`${player}:${id}`);
}

/** Shows `g` (or queues it behind the one shown) unless `player` has seen
 *  it; returns whether it will show. */
export function showGuide(player: string, g: Guide): boolean {
  const all = seen();
  const key = `${player}:${g.id}`;
  if (all.has(key)) return false;
  all.add(key);
  try {
    localStorage.setItem(STORE, JSON.stringify([...all]));
  } catch {
    // Not remembered: fine.
  }
  if (guide()) waiting.push(g);
  else setGuide(g);
  return true;
}

/** Puts the shown guide away (the next waiting one takes its place). */
function dismiss(): void {
  setGuide(waiting.shift() ?? null);
}

/** Puts the shown guide away (Esc); returns whether one was shown. */
export function closeGuide(): boolean {
  const open = guide() !== null;
  dismiss();
  return open;
}

/** The free builds, at the start. */
export function freeStartGuide(free: Record<string, number>, show: () => void): Guide {
  const list = Object.entries(free)
    .filter(([k, n]) => n > 0 && FREE_KINDS[k])
    .map(([k, n]) => `${n} ${FREE_KINDS[k][0]}`)
    .join(", ");
  return {
    id: "free-start",
    title: "Your first ones are free",
    lines: [
      <>Free until you've built them: {list}.</>,
      <>A start: iron and coal mines, a steelworks, and track joining them.</>,
      <>Track is free until your steel production is running. The build menu counts what's still free.</>,
    ],
    show,
  };
}

/** Track stops being free: steel production is running. */
export function trackCostsGuide(show: () => void): Guide {
  return {
    id: "free-track-ended",
    title: "Steel is running",
    lines: [<>Your steelworks has made steel: track now costs 5 steel a hex.</>],
    show,
  };
}

/** A kind down to its last free one. */
export function lastFreeGuide(kind: string, show: () => void): Guide {
  const [, cost, one] = FREE_KINDS[kind] ?? [kind, 0, kind];
  return {
    id: `free-last-${kind}`,
    title: `Last free ${one}`,
    lines: [<>The next {one} costs {cost} steel.</>],
    show,
  };
}

/** Free kinds used up (several at once in one card). */
export function freeEndGuide(kinds: string[], show: () => void): Guide {
  const parts = kinds.map((k) => `${FREE_KINDS[k]?.[0] ?? k} now cost ${FREE_KINDS[k]?.[1] ?? "?"} steel`);
  return {
    id: `free-end-${[...kinds].sort().join("+")}`,
    title: kinds.length === 1 ? `Free ${FREE_KINDS[kinds[0]]?.[0] ?? kinds[0]} used up` : "Free builds used up",
    lines: [
      <>{parts.join("; ").replace(/^./, (c) => c.toUpperCase())}.</>,
      <>Steel comes from a steelworks (iron ore and coal); your home makes 10 a minute while you hold under 30.</>,
    ],
    show,
  };
}

/** Losing every depot. */
export function respawnGuide(units: number, home?: [number, number]): Guide {
  return {
    id: "respawn",
    title: "You died. What now?",
    lines: [
      <>Every depot of yours was captured.</>,
      <>You start again at a new home, far from everyone, with {units === 1 ? "1 unit" : `${units} units`} and all your research.</>,
      <>Place a mine where the ground glows, and join it to home with track.</>,
    ],
    at: home,
  };
}

/** A site stuck for steel, the first time. */
export function steelGuide(site: string, at: [number, number]): Guide {
  return {
    id: "no-steel",
    title: "Out of steel",
    lines: [
      <>Building costs steel, and your stations have none: your {site.toLowerCase()} has stopped.</>,
      <>A steelworks makes it from iron ore and coal. Build one, and mines for both.</>,
    ],
    at,
  };
}

/** A plant starved of an input, the first time. */
export function starvedGuide(plant: string, good: string, at?: [number, number]): Guide {
  return {
    id: "starved",
    title: "A plant stopped",
    lines: [
      <>Your {plant.toLowerCase()} is out of {good.toLowerCase()}: it waits for more at its station.</>,
      <>Mines dig into their own station; track carries the ore on to plants.</>,
      <>Hover a ⚠ to see what anything of yours is waiting for.</>,
    ],
    at,
  };
}

/** The first contact with an enemy (shown once the fighting calms). */
export function contactGuide(fight: string): Guide {
  return {
    id: "contact",
    title: "First contact",
    lines: [
      <>Your units have met an enemy's.</>,
      <>
        <kbd>{fight}</kbd> then a click: they fight their way there.
      </>,
      <>Units out of ammunition are tinted. Rounds come by rail, so keep the front joined to home.</>,
    ],
  };
}

/** Enemies in sight and nobody firing: units keep the peace until told. */
export function peaceGuide(fight: string): Guide {
  return {
    id: "peace",
    title: "Your units keep the peace",
    lines: [
      <>They hold fire unless they're fired on, or someone comes onto your ground.</>,
      <>
        To attack, <kbd>{fight}</kbd> then a click (they fight their way there), or right-click their units.
      </>,
    ],
  };
}

/** The first structure of theirs taken (shown once the fighting calms). */
export function capturedGuide(name: string, at: [number, number]): Guide {
  return {
    id: "captured",
    title: `They took your ${name.toLowerCase()}`,
    lines: [
      <>Their armed units stood within two hexes of it, and none of yours did.</>,
      <>Drive them off and stand your own beside it to take it back.</>,
    ],
    at,
  };
}

export function GuideScreen(): JSX.Element {
  return (
    <Show when={guide()}>
      {(g) => (
        <aside class="guide-card" aria-label={g().title}>
          <div class="guide-head">
            <h2>{g().title}</h2>
            <button class="ghost guide-close" aria-label="Close" onClick={dismiss}>
              ×
            </button>
          </div>
          <For each={g().lines}>{(l) => <p>{l}</p>}</For>
          <div class="guide-actions">
            <Show when={g().at || g().show}>
              <button class="ghost" onClick={() => (g().show ? g().show!() : look(g().at!))}>
                Show me
              </button>
            </Show>
            <Show
              when={g().action}
              fallback={
                <button class="primary" onClick={dismiss}>
                  Got it
                </button>
              }
            >
              {(a) => (
                <>
                  <button class="ghost" onClick={dismiss}>
                    Later
                  </button>
                  <button
                    class="primary"
                    onClick={() => {
                      a().run();
                      dismiss();
                    }}
                  >
                    {a().label}
                  </button>
                </>
              )}
            </Show>
          </div>
        </aside>
      )}
    </Show>
  );
}
